import axios from 'axios';
import { API_BASE_URL, API_TIMEOUT } from '../config/api';

// DEMO MODE - Backend olmadan test için
const DEMO_MODE = false; // AWS Backend aktif!

// Axios instance oluştur
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Demo veriler
const demoVeliler = [];

/**
 * Kullanıcı Login API
 * @param {string} kullaniciAdi - Kullanıcı adı
 * @param {string} sifre - Şifre
 * @returns {Promise} API yanıtı
 */
export const kullaniciLogin = async (kullaniciAdi, sifre) => {
  try {
    const response = await api.post('/kullanici-login', {
      kullaniciAdi,
      sifre
    });
    return response.data;
  } catch (error) {
    console.error('Login hatası:', error);
    return {
      success: false,
      mesaj: error.response?.data?.mesaj || 'Giriş başarısız',
    };
  }
};

/**
 * Liveness Session Oluşturma API
 * @param {string} okulId - Okul ID (multi-tenant)
 * @returns {Promise} Session ID
 */
export const livenessSessionOlustur = async (okulId = null) => {
  try {
    const userStr = localStorage.getItem('kres_user');
    const user = userStr ? JSON.parse(userStr) : null;

    const payload = {};
    if (okulId) payload.okulId = okulId;
    if (user?.kullaniciId) payload.kullaniciId = user.kullaniciId;

    const response = await api.post('/liveness-session-olustur', payload);
    return response.data;
  } catch (error) {
    console.error('Liveness session oluşturma hatası:', error);
    return {
      success: false,
      mesaj: error.response?.data?.mesaj || 'Session oluşturulamadı',
    };
  }
};

/**
 * Yüz Tanıma API (Liveness ile)
 * @param {string} imageBase64 - Base64 formatında fotoğraf
 * @param {string} okulId - Okul ID (multi-tenant)
 * @param {string} collectionId - Rekognition collection ID (multi-tenant)
 * @param {string} sessionId - Liveness session ID (opsiyonel)
 * @returns {Promise} API yanıtı
 */
export const yuzTani = async (imageBase64, okulId = null, collectionId = null, sessionId = null) => {
  // DEMO MODE
  if (DEMO_MODE) {
    await new Promise(resolve => setTimeout(resolve, 1500)); // Simüle gecikme

    if (demoVeliler.length === 0) {
      return {
        success: false,
        mesaj: 'Henüz kayıtlı veli yok. Admin panelden veli ekleyin.',
      };
    }

    // Rastgele bir veli döndür
    const randomVeli = demoVeliler[Math.floor(Math.random() * demoVeliler.length)];
    return {
      success: true,
      cocukAdi: randomVeli.cocukAdi,
      veliAdi: randomVeli.veliAdi,
      ogretmenEmail: randomVeli.ogretmenEmail,
      mesaj: 'Yüz tanındı! Çocuk teslim alınabilir.',
    };
  }

  // Gerçek API çağrısı
  try {
    // Kullanıcı bilgisini localStorage'dan al
    const userStr = localStorage.getItem('kres_user');
    const user = userStr ? JSON.parse(userStr) : null;

    const payload = { fotografBase64: imageBase64 };
    if (okulId) payload.okulId = okulId;
    if (collectionId) payload.collectionId = collectionId;
    if (sessionId) payload.sessionId = sessionId; // Liveness session ID
    if (user?.kullaniciId) payload.kullaniciId = user.kullaniciId;
    if (user?.role) payload.userRole = user.role; // Super Admin için tüm collection'larda ara

    const response = await api.post('/yuz-tanima', payload);
    return response.data;
  } catch (error) {
    console.error('Yüz tanıma hatası:', error);
    return {
      success: false,
      mesaj: error.response?.data?.mesaj || 'Yüz tanıma başarısız',
    };
  }
};

/**
 * Veli Kayıt API
 * @param {Object} veliData - Veli bilgileri
 * @param {string} okulId - Okul ID (multi-tenant)
 * @param {string} collectionId - Rekognition collection ID (multi-tenant)
 * @returns {Promise} API yanıtı
 */
export const veliKaydet = async (veliData, okulId = null, collectionId = null) => {
  // DEMO MODE
  if (DEMO_MODE) {
    await new Promise(resolve => setTimeout(resolve, 1000)); // Simüle gecikme

    // Demo veri ekle
    demoVeliler.push({
      cocukAdi: veliData.cocukAdi,
      veliAdi: veliData.veliAdi,
      ogretmenEmail: veliData.ogretmenEmail,
    });

    console.log('Demo veli kaydedildi:', veliData);
    console.log('Toplam kayıtlı veli sayısı:', demoVeliler.length);

    return {
      success: true,
      mesaj: `${veliData.veliAdi} başarıyla kaydedildi! (Demo Mode - ${demoVeliler.length} veli kayıtlı)`,
    };
  }

  // Gerçek API çağrısı
  try {
    // Kullanıcı bilgisini localStorage'dan al
    const userStr = localStorage.getItem('kres_user');
    const user = userStr ? JSON.parse(userStr) : null;

    const payload = { ...veliData };
    if (okulId) payload.okulId = okulId;
    if (collectionId) payload.collectionId = collectionId;
    if (user?.kullaniciId) payload.kullaniciId = user.kullaniciId;

    const response = await api.post('/veli-kayit', payload);
    return response.data;
  } catch (error) {
    console.error('Veli kayıt hatası:', error);
    return {
      success: false,
      mesaj: error.response?.data?.mesaj || 'Veli kaydı başarısız',
    };
  }
};

/**
 * Veli Listeleme API
 * @param {string} okulId - Okul ID (multi-tenant filtering)
 * @returns {Promise} Kayıtlı velilerin listesi
 */
export const veliListele = async (okulId = null) => {
  // DEMO MODE
  if (DEMO_MODE) {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      success: true,
      veliler: demoVeliler,
      toplam: demoVeliler.length,
    };
  }

  // Gerçek API çağrısı
  try {
    const url = okulId ? `/veli-listele?okulId=${okulId}` : '/veli-listele';
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Veli listeleme hatası:', error);
    return {
      success: false,
      mesaj: error.response?.data?.mesaj || 'Veli listesi alınamadı',
      veliler: [],
    };
  }
};

/**
 * Veli Silme API
 * @param {string} veliId - Silinecek veli ID
 * @returns {Promise} API yanıtı
 */
export const veliSil = async (veliId) => {
  // DEMO MODE
  if (DEMO_MODE) {
    await new Promise(resolve => setTimeout(resolve, 500));
    const index = demoVeliler.findIndex(v => v.veliId === veliId);
    if (index !== -1) {
      demoVeliler.splice(index, 1);
    }
    return {
      success: true,
      mesaj: 'Veli kaydı silindi',
    };
  }

  // Gerçek API çağrısı
  try {
    // Kullanıcı bilgisini localStorage'dan al
    const userStr = localStorage.getItem('kres_user');
    const user = userStr ? JSON.parse(userStr) : null;

    const payload = { veliId };
    if (user?.kullaniciId) payload.kullaniciId = user.kullaniciId;

    const response = await api.post('/veli-sil', payload);
    return response.data;
  } catch (error) {
    console.error('Veli silme hatası:', error);
    return {
      success: false,
      mesaj: error.response?.data?.mesaj || 'Veli silinemedi',
    };
  }
};

/**
 * Veli Güncelleme API
 * @param {Object} veliData - Güncellenmiş veli bilgileri (veliId, cocukAdi, veliAdi, veliTelefon, ogretmenEmail, fotografBase64?)
 * @returns {Promise} API yanıtı
 */
export const veliGuncelle = async (veliData) => {
  // DEMO MODE
  if (DEMO_MODE) {
    await new Promise(resolve => setTimeout(resolve, 1000));
    const index = demoVeliler.findIndex(v => v.veliId === veliData.veliId);
    if (index !== -1) {
      demoVeliler[index] = { ...demoVeliler[index], ...veliData };
    }
    return {
      success: true,
      mesaj: 'Veli bilgileri güncellendi',
    };
  }

  // Gerçek API çağrısı
  try {
    // Kullanıcı bilgisini localStorage'dan al
    const userStr = localStorage.getItem('kres_user');
    const user = userStr ? JSON.parse(userStr) : null;

    const payload = { ...veliData };
    if (user?.kullaniciId) payload.kullaniciId = user.kullaniciId;

    const response = await api.post('/veli-guncelle', payload);
    return response.data;
  } catch (error) {
    console.error('Veli güncelleme hatası:', error);
    return {
      success: false,
      mesaj: error.response?.data?.mesaj || 'Veli güncellenemedi',
    };
  }
};

/**
 * Okul Ekleme API (Super Admin)
 * @param {Object} okulData - Okul bilgileri
 * @returns {Promise} API yanıtı
 */
export const okulEkle = async (okulData) => {
  try {
    const response = await api.post('/okul-ekle', okulData);
    return response.data;
  } catch (error) {
    console.error('Okul ekleme hatası:', error);
    return {
      success: false,
      mesaj: error.response?.data?.mesaj || 'Okul eklenemedi',
    };
  }
};

/**
 * Okul Listeleme API (Super Admin)
 * @returns {Promise} Kayıtlı okulların listesi
 */
export const okulListele = async () => {
  try {
    const response = await api.get('/okul-listele');
    return response.data;
  } catch (error) {
    console.error('Okul listeleme hatası:', error);
    return {
      success: false,
      mesaj: error.response?.data?.mesaj || 'Okul listesi alınamadı',
      okullar: [],
    };
  }
};

/**
 * Okul Silme API (Super Admin)
 * @param {string} okulId - Silinecek okul ID
 * @returns {Promise} API yanıtı
 */
export const okulSil = async (okulId) => {
  try {
    const response = await api.post('/okul-sil', { okulId });
    return response.data;
  } catch (error) {
    console.error('Okul silme hatası:', error);
    return {
      success: false,
      mesaj: error.response?.data?.mesaj || 'Okul silinemedi',
    };
  }
};

/**
 * Okul Güncelleme API (Super Admin)
 * @param {Object} okulData - Güncellenecek okul bilgileri
 * @returns {Promise} API yanıtı
 */
export const okulGuncelle = async (okulData) => {
  try {
    const response = await api.post('/okul-guncelle', okulData);
    return response.data;
  } catch (error) {
    console.error('Okul güncelleme hatası:', error);
    return {
      success: false,
      mesaj: error.response?.data?.mesaj || 'Okul güncellenemedi',
    };
  }
};

/**
 * Log Listeleme API (Super Admin)
 * @param {string} okulId - Opsiyonel: Belirli bir okul için loglar
 * @returns {Promise} Log listesi
 */
export const logListele = async (okulId = null) => {
  try {
    const url = okulId ? `/log-listele?okulId=${okulId}` : '/log-listele';
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Log listeleme hatası:', error);
    return {
      success: false,
      mesaj: error.response?.data?.mesaj || 'Log listesi alınamadı',
      loglar: [],
    };
  }
};

/**
 * Şifre Değiştirme API
 * @param {string} kullaniciAdi - Kullanıcı adı
 * @param {string} eskiSifre - Eski şifre
 * @param {string} yeniSifre - Yeni şifre
 * @returns {Promise} API yanıtı
 */
export const sifreDegistir = async (kullaniciAdi, eskiSifre, yeniSifre) => {
  try {
    const response = await api.post('/sifre-degistir', {
      kullaniciAdi,
      eskiSifre,
      yeniSifre
    });
    return response.data;
  } catch (error) {
    console.error('Şifre değiştirme hatası:', error);
    return {
      success: false,
      mesaj: error.response?.data?.mesaj || 'Şifre değiştirilemedi',
    };
  }
};

/**
 * Şifre Sıfırlama Talebi API
 * @param {string} kullaniciAdi - Kullanıcı adı
 * @param {string} email - E-posta adresi
 * @returns {Promise} API yanıtı
 */
export const sifreSifirlamaTalep = async (kullaniciAdi, email) => {
  try {
    const response = await api.post('/sifre-sifirlama-talep', {
      kullaniciAdi,
      email
    });
    return response.data;
  } catch (error) {
    console.error('Şifre sıfırlama talebi hatası:', error);
    return {
      success: false,
      mesaj: error.response?.data?.mesaj || 'Şifre sıfırlama talebi gönderilemedi',
    };
  }
};

/**
 * Şifre Sıfırlama Uygulama API
 * @param {string} token - Şifre sıfırlama token'ı
 * @param {string} yeniSifre - Yeni şifre
 * @returns {Promise} API yanıtı
 */
export const sifreSifirlamaUygula = async (token, yeniSifre) => {
  try {
    const response = await api.post('/sifre-sifirlama-uygula', {
      token,
      yeniSifre
    });
    return response.data;
  } catch (error) {
    console.error('Şifre sıfırlama uygulama hatası:', error);
    return {
      success: false,
      mesaj: error.response?.data?.mesaj || 'Şifre güncellenemedi',
    };
  }
};

export default api;
